import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import Navigation from '../components/Navigation';
import TutorialHint from '../components/TutorialHint';
import { formatLocalDate, parseLocalDate } from '../utils/date';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { BookOpen, Calendar, HelpCircle, ChevronRight, Users, ChevronLeft, Home, Save, Edit3, History, FileText, Download } from 'lucide-react';
import FavoriteStar from '../components/FavoriteStar';

/**
 * Strip any trailing title/description from a sermon passage so the Bible Gateway
 * URL only contains the scripture reference.
 *
 * Examples:
 *   "Matthew 22:15-22 — Jesus and the Third Way"  →  "Matthew 22:15-22"
 *   "Acts 18:24-28 - Apollos in Ephesus"          →  "Acts 18:24-28"
 *   "Acts 19:23–20:1"                              →  "Acts 19:23–20:1"  (kept — no surrounding spaces)
 *   "Acts 19:8-22"                                 →  "Acts 19:8-22"
 */
const extractReference = (passage) => {
  if (!passage) return '';
  // Split on em-dash, en-dash, or hyphen ONLY when surrounded by spaces (true separators).
  // This preserves verse ranges like "19:23–20:1" or "18:24-28".
  return passage.split(/\s[—–-]\s/)[0].trim();
};

const SermonNotes = () => {
  const { user, API } = useContext(AuthContext);
  const [currentSermon, setCurrentSermon] = useState(null);
  const [allSermons, setAllSermons] = useState([]);
  const [selectedWeekDate, setSelectedWeekDate] = useState(null); // null means current week
  const [loading, setLoading] = useState(true);
  const [sermonNotes, setSermonNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [myNotes, setMyNotes] = useState([]);
  const [loadingMyNotes, setLoadingMyNotes] = useState(false);
  const [activeTab, setActiveTab] = useState('current'); // 'current' or 'history'
  const [exporting, setExporting] = useState(false);

  const handleExportNotes = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/sermon-notes/export`);
      const { filename, content, notes_count } = response.data;
      
      if (notes_count === 0) {
        toast.error('No notes to export');
        return;
      }
      
      // Create blob and download
      const blob = new Blob([content], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported ${notes_count} sermon notes!`);
    } catch (error) {
      toast.error('Failed to export notes');
    } finally {
      setExporting(false);
    }
  };

  const fetchMyNotes = useCallback(async () => {
    setLoadingMyNotes(true);
    try {
      const response = await axios.get(`${API}/sermon-notes`);
      setMyNotes(response.data);
    } catch (error) {
      console.error('Error fetching my notes:', error);
    } finally {
      setLoadingMyNotes(false);
    }
  }, [API]);

  const fetchNotesForWeek = useCallback(async (weekDate) => {
    try {
      const response = await axios.get(`${API}/sermon-notes/${weekDate}`);
      if (response.data) {
        setSermonNotes(response.data.notes || '');
      } else {
        setSermonNotes('');
      }
    } catch (error) {
      console.error('Error fetching notes for week:', error);
      setSermonNotes('');
    }
  }, [API]);

  const fetchAllSermons = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/sermons/all`);
      setAllSermons(response.data);
    } catch (error) {
      console.error('Error fetching all sermons:', error);
    }
  }, [API]);

  const fetchSermonByDate = useCallback(async (weekDate) => {
    try {
      const response = await axios.get(`${API}/sermons/by-date/${weekDate}`);
      setCurrentSermon(response.data);
    } catch (error) {
      console.error('Error fetching sermon:', error);
      toast.error('Failed to load sermon');
    }
  }, [API]);

  const fetchCurrentSermon = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/scripture/current`);
      setCurrentSermon(response.data);
      if (response.data) {
        setSelectedWeekDate(response.data.week_start_date);
      }
    } catch (error) {
      console.error('Error fetching sermon:', error);
      toast.error('Failed to load sermon');
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => {
    fetchCurrentSermon();
    fetchAllSermons();
  }, [fetchCurrentSermon, fetchAllSermons]);

  // Deep-link from global search: /sermon?date=YYYY-MM-DD → jump to that sermon.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const target = params.get('date');
    if (!target) return;
    setSelectedWeekDate(target);
    fetchSermonByDate(target);
    // Strip the query param so refreshes don't keep re-jumping.
    const clean = new URL(window.location.href);
    clean.searchParams.delete('date');
    window.history.replaceState({}, '', clean.toString());
  }, [fetchSermonByDate]);

  // Load notes when selectedWeekDate changes
  useEffect(() => {
    if (selectedWeekDate) {
      fetchNotesForWeek(selectedWeekDate);
    }
  }, [selectedWeekDate, fetchNotesForWeek]);

  const handleWeekChange = (weekDate) => {
    if (weekDate === 'current') {
      fetchCurrentSermon();
    } else {
      setSelectedWeekDate(weekDate);
      fetchSermonByDate(weekDate);
    }
  };

  const getCurrentWeekIndex = () => {
    if (!selectedWeekDate || allSermons.length === 0) return -1;
    return allSermons.findIndex(s => s.week_start_date === selectedWeekDate);
  };

  const goToPreviousWeek = () => {
    const currentIndex = getCurrentWeekIndex();
    if (currentIndex > 0) {
      handleWeekChange(allSermons[currentIndex - 1].week_start_date);
    }
  };

  const goToNextWeek = () => {
    const currentIndex = getCurrentWeekIndex();
    if (currentIndex < allSermons.length - 1 && currentIndex !== -1) {
      handleWeekChange(allSermons[currentIndex + 1].week_start_date);
    }
  };

  const isCurrentWeek = () => {
    if (!currentSermon || !selectedWeekDate) return false;
    const today = new Date();
    const daysSinceSunday = (today.getDay() === 0 ? 0 : today.getDay());
    const sunday = new Date(today);
    sunday.setDate(today.getDate() - daysSinceSunday);
    const currentWeekDate = sunday.toISOString().split('T')[0];
    return selectedWeekDate === currentWeekDate;
  };

  const formatDate = (dateStr) => formatLocalDate(dateStr, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  const getWeekRange = (dateStr) => {
    const sunday = parseLocalDate(dateStr);
    const saturday = new Date(sunday);
    saturday.setDate(sunday.getDate() + 6);

    return `Sun, ${sunday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${saturday.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50">
        <Navigation />
        <div className="flex items-center justify-center h-[80vh]">
          <div className="text-lg text-slate-600">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50" data-testid="sermon-notes-page">
      <Navigation />
      
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-8 animate-fade-in text-center">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#2B6B7F] to-[#5FA8C1] flex items-center justify-center mx-auto mb-4">
            <BookOpen className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-[#2B6B7F] mb-3">
            Weekly Sermon Notes
          </h1>
          <div className="flex items-center justify-center gap-3 mb-2">
            <TutorialHint videoId="iG4BqMldGbg" />
            <p className="text-slate-600">Reflect on this week&apos;s sermon and grow in God&apos;s Word</p>
          </div>
        </div>

        {/* Main Tabs - Current Sermon vs My Notes History */}
        <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); if (v === 'history') fetchMyNotes(); }} className="mb-6">
          <TabsList className="grid grid-cols-2 h-auto bg-slate-100 p-1 rounded-lg mb-6">
            <TabsTrigger 
              value="current" 
              className="flex items-center gap-2 py-3 data-[state=active]:bg-white data-[state=active]:shadow-sm"
            >
              <Edit3 className="w-4 h-4 text-[#5FA8C1]" />
              <span className="font-medium">Current Sermon</span>
            </TabsTrigger>
            <TabsTrigger 
              value="history" 
              className="flex items-center gap-2 py-3 data-[state=active]:bg-white data-[state=active]:shadow-sm"
            >
              <History className="w-4 h-4 text-[#7BA888]" />
              <span className="font-medium">My Past Notes</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="history">
            {loadingMyNotes ? (
              <Card>
                <CardContent className="pt-6 text-center py-12">
                  <div className="text-lg text-slate-600">Loading your notes...</div>
                </CardContent>
              </Card>
            ) : myNotes.length === 0 ? (
              <Card>
                <CardContent className="pt-6 text-center py-12">
                  <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-700 mb-2">
                    No Saved Notes Yet
                  </h3>
                  <p className="text-slate-600 mb-4">
                    Start taking notes on sermons to see them here
                  </p>
                  <Button onClick={() => setActiveTab('current')} className="bg-[#2B6B7F]">
                    Go to Current Sermon
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* Export Button */}
                <div className="flex justify-end mb-4">
                  <Button
                    onClick={handleExportNotes}
                    variant="outline"
                    disabled={exporting}
                    className="border-green-500 text-green-700 hover:bg-green-50"
                    data-testid="export-sermon-notes-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {exporting ? 'Exporting...' : 'Export All Notes'}
                  </Button>
                </div>
                
                {myNotes.map((note) => (
                  <Card key={note.id} className="border-l-4 border-l-[#5FA8C1]">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-xl text-[#2B6B7F]">{note.passage}</CardTitle>
                          <CardDescription className="flex items-center gap-2 mt-1">
                            <Calendar className="w-4 h-4" />
                            Week of {getWeekRange(note.week_start_date)}
                          </CardDescription>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setActiveTab('current');
                            handleWeekChange(note.week_start_date);
                          }}
                          className="text-[#2B6B7F] border-[#2B6B7F]"
                        >
                          <Edit3 className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">
                        {note.notes}
                      </p>
                      <p className="text-xs text-slate-400 mt-4">
                        Last updated: {new Date(note.updated_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit'
                        })}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="current">
        {/* Week Navigation */}
        {allSermons.length > 0 && (
          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="flex flex-col items-center gap-3">
                {/* Previous Week Button */}
                <Button
                  onClick={goToPreviousWeek}
                  disabled={getCurrentWeekIndex() <= 0}
                  variant="outline"
                  size="sm"
                  className="w-full max-w-xs"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous Week
                </Button>
                
                {/* Current Week Info */}
                <div className="text-center py-2">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    {!isCurrentWeek() && (
                      <Button
                        onClick={() => handleWeekChange('current')}
                        variant="outline"
                        size="sm"
                        className="text-[#2B6B7F] border-[#2B6B7F]"
                      >
                        <Home className="w-4 h-4 mr-1" />
                        Go to Current Week
                      </Button>
                    )}
                    {isCurrentWeek() && (
                      <Badge className="bg-green-500 text-white">Current Week</Badge>
                    )}
                  </div>
                  {currentSermon && (
                    <p className="text-sm text-slate-600">
                      Week of {getWeekRange(currentSermon.week_start_date)}
                    </p>
                  )}
                </div>
                
                {/* Next Week Button */}
                <Button
                  onClick={goToNextWeek}
                  disabled={getCurrentWeekIndex() >= allSermons.length - 1 || getCurrentWeekIndex() === -1}
                  variant="outline"
                  size="sm"
                  className="w-full max-w-xs"
                >
                  Next Week
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {!currentSermon ? (
          <Card>
            <CardContent className="pt-6 text-center py-12">
              <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">
                No Sermon Available Yet
              </h3>
              <p className="text-slate-600">
                Check back soon for this week's sermon discussion questions
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Current Week Header */}
            <Card className="mb-8 border-t-4 border-t-[#2B6B7F]">
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 rounded-full bg-[#2B6B7F]/10 flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-6 h-6 text-[#2B6B7F]" />
                  </div>
                  <div className="min-w-0">
                    <Badge className="bg-[#2B6B7F] mb-2">This Week</Badge>
                    <CardTitle className="text-3xl font-heading text-[#2B6B7F] break-words">
                      {currentSermon.passage}
                    </CardTitle>
                    <div className="mt-2">
                      <FavoriteStar
                        kind="sermon"
                        itemId={currentSermon.id}
                        itemPayload={{
                          id: currentSermon.id,
                          passage: currentSermon.passage,
                          week_start_date: currentSermon.week_start_date,
                          scripture_text: currentSermon.scripture_text,
                        }}
                        label="Star"
                      />
                    </div>
                    <CardDescription className="text-base mt-2">
                      Week of {getWeekRange(currentSermon.week_start_date)}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
            </Card>

            {/* Scripture Passage Card */}
            <Card className="mb-6">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <BookOpen className="w-6 h-6 text-[#5FA8C1]" />
                  <CardTitle className="text-2xl font-heading text-[#2B6B7F]">
                    Read the Passage
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="bg-gradient-to-br from-blue-50 to-teal-50 p-6 rounded-lg border border-blue-200">
                  <p className="text-lg font-semibold text-slate-800 mb-3">
                    {currentSermon.passage}
                  </p>
                  <p className="text-slate-700 leading-relaxed mb-4">
                    Open your Bible and read this passage. Take time to reflect on what God is speaking to you through His Word.
                  </p>
                  <a
                    href={`https://www.biblegateway.com/passage/?search=${encodeURIComponent(extractReference(currentSermon.passage))}&version=NIV`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[#2B6B7F] hover:text-[#1E4D5F] font-medium"
                  >
                    Read on BibleGateway.com
                    <ChevronRight className="w-4 h-4" />
                  </a>
                </div>
              </CardContent>
            </Card>

            {/* Study Questions Card */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <HelpCircle className="w-6 h-6 text-[#7BA888]" />
                  <div>
                    <CardTitle className="text-2xl font-heading text-[#2B6B7F]">
                      Study Questions
                    </CardTitle>
                    <CardDescription>
                      Reflect on these questions as you study this week
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {currentSermon.study_questions.map((question, index) => (
                    <div
                      key={`sq-${index}-${question.slice(0, 20)}`}
                      className="p-4 rounded-lg bg-slate-50 border border-slate-200"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#7BA888] text-white flex items-center justify-center font-semibold flex-shrink-0">
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <p className="text-slate-800 leading-relaxed">{question}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 p-4 rounded-lg bg-[#2B6B7F]/10 border border-[#2B6B7F]/20">
                  <p className="text-sm text-slate-700">
                    <strong>Tip:</strong> Journal your reflections on these questions in your daily journal under "Engage Scripture" to deepen your understanding and track your spiritual growth.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Parent Questions Section */}
            {currentSermon.parent_questions && currentSermon.parent_questions.length > 0 && (
              <Card className="mt-6 border-l-4 border-l-[#D4A574]">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <Users className="w-6 h-6 text-[#D4A574]" />
                    <div>
                      <CardTitle className="text-2xl font-heading text-[#2B6B7F]">
                        Questions for Parents & Kids
                      </CardTitle>
                      <CardDescription>
                        Age-appropriate questions (ages 5-10) to discuss with your children
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="parent-questions">
                      <AccordionTrigger className="text-lg font-medium text-[#2B6B7F] hover:text-[#1E4D5F]">
                        View Parent Discussion Questions
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-3 pt-2">
                          {currentSermon.parent_questions.map((question, index) => (
                            <div
                              key={`pq-${index}-${question.slice(0, 20)}`}
                              className="p-4 rounded-lg bg-[#D4A574]/10 border border-[#D4A574]/30"
                            >
                              <div className="flex items-start gap-3">
                                <div className="w-8 h-8 rounded-full bg-[#D4A574] text-white flex items-center justify-center font-semibold flex-shrink-0">
                                  {index + 1}
                                </div>
                                <div className="flex-1">
                                  <p className="text-slate-800 leading-relaxed">{question}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                          
                          <div className="mt-4 p-4 rounded-lg bg-blue-50 border border-blue-200">
                            <p className="text-sm text-slate-700">
                              <strong>Parent Tip:</strong> Use these questions during family meals, bedtime, or car rides to help your children understand and apply God's Word. Keep conversations casual and age-appropriate!
                            </p>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </CardContent>
              </Card>
            )}

            {/* Personal Sermon Notes */}
            <Card className="mt-6 border-l-4 border-l-[#5FA8C1]">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Edit3 className="w-6 h-6 text-[#5FA8C1]" />
                  <div>
                    <CardTitle className="text-2xl font-heading text-[#2B6B7F]">
                      My Sermon Notes
                    </CardTitle>
                    <CardDescription>
                      Capture your thoughts, reflections, and insights from this week's sermon
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="What stood out to you in this sermon? What is God teaching you? How will you apply this to your life?"
                  value={sermonNotes}
                  onChange={(e) => setSermonNotes(e.target.value)}
                  className="min-h-[200px] resize-none"
                  rows={8}
                />
                <div className="mt-4 flex items-center gap-3">
                  <Button
                    onClick={async () => {
                      setSavingNotes(true);
                      try {
                        await axios.post(`${API}/sermon-notes`, {
                          week_start_date: currentSermon.week_start_date,
                          passage: currentSermon.passage,
                          notes: sermonNotes
                        });
                        toast.success('Notes saved!');
                        // Refresh my notes list
                        fetchMyNotes();
                      } catch (error) {
                        toast.error('Failed to save notes');
                      } finally {
                        setSavingNotes(false);
                      }
                    }}
                    disabled={savingNotes || !sermonNotes.trim()}
                    className="bg-[#5FA8C1]"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {savingNotes ? 'Saving...' : 'Save Notes'}
                  </Button>
                  <p className="text-xs text-slate-500">
                    Notes are saved to your account
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Group Discussion Prompt */}
            <Card className="mt-6 bg-gradient-to-br from-teal-50 to-blue-50 border-blue-200">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#5FA8C1] flex items-center justify-center flex-shrink-0">
                    <BookOpen className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-800 mb-2">
                      Discuss with Your Small Group
                    </h4>
                    <p className="text-sm text-slate-700 leading-relaxed">
                      These questions are perfect for small group discussions! Bring your insights and learn from others as you explore God's Word together. Don't forget to share what God has been teaching you.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default SermonNotes;
